import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'hidden', 'list', 'option'];

  static values = {
    url: String,
    delay: { type: Number, default: 300 },
    threshold: { type: Number, default: 2 },
  };

  connect() {
    this.timeout = null;
    this.pending = null;
    this.activeIndex = -1;
  }

  disconnect() {
    this.#cancel();
    this.#abort();
  }

  search() {
    this.#clearSelection();
    this.#schedule();
  }

  openOnFocus() {
    if (this.hiddenTarget.value) return;

    this.#schedule();
  }

  navigate(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.#opened()) this.#move(1);
        else this.#schedule();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.#opened()) this.#move(-1);
        break;
      case 'Enter':
        if (!this.#opened()) return;
        event.preventDefault();
        this.#activeOption()?.click();
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  }

  choose(event) {
    const { investorId, investorName, investorBalanceCents } = event.currentTarget.dataset;

    this.inputTarget.value = investorName;
    this.hiddenTarget.value = investorId;
    this.hiddenTarget.dataset.investorName = investorName;
    this.hiddenTarget.dataset.investorBalanceCents = investorBalanceCents;
    this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }));

    this.close();
  }

  keepFocus(event) {
    event.preventDefault();
  }

  highlight(event) {
    this.#activate(this.optionTargets.indexOf(event.currentTarget));
  }

  closeOnOutsideClick(event) {
    if (this.element.contains(event.target)) return;

    this.close();
  }

  closeOnFocusOut(event) {
    if (this.element.contains(event.relatedTarget)) return;

    this.close();
  }

  close() {
    this.#cancel();
    this.#abort();

    this.listTarget.innerHTML = '';
    this.listTarget.classList.add('d-none');
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.inputTarget.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  #schedule() {
    this.#cancel();

    const term = this.inputTarget.value.trim();

    if (term.length > 0 && term.length < this.thresholdValue) {
      this.close();
      return;
    }

    this.timeout = setTimeout(() => this.#fetch(term), this.delayValue);
  }

  async #fetch(term) {
    this.#abort();

    const request = new AbortController();
    this.pending = request;

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('term', term);

    try {
      const response = await fetch(url, {
        signal: request.signal,
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        this.close();
        return;
      }

      this.listTarget.innerHTML = await response.text();
      this.#open();
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      if (this.pending === request) this.pending = null;
    }
  }

  #open() {
    this.listTarget.classList.remove('d-none');
    this.inputTarget.setAttribute('aria-expanded', 'true');
    this.#activate(this.optionTargets.length > 0 ? 0 : -1);
  }

  #activate(index) {
    this.optionTargets.forEach((option, position) => {
      const active = position === index;

      option.classList.toggle('bg-primary-100', active);
      option.setAttribute('aria-selected', String(active));
    });

    this.activeIndex = index;

    const option = this.optionTargets[index];

    if (!option) {
      this.inputTarget.removeAttribute('aria-activedescendant');
      return;
    }

    this.inputTarget.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  #move(step) {
    const { length } = this.optionTargets;

    if (length === 0) return;

    this.#activate((this.activeIndex + step + length) % length);
  }

  #activeOption() {
    return this.optionTargets[this.activeIndex];
  }

  #opened() {
    return !this.listTarget.classList.contains('d-none');
  }

  #clearSelection() {
    if (!this.hiddenTarget.value) return;

    this.hiddenTarget.value = '';
    this.hiddenTarget.dataset.investorName = '';
    this.hiddenTarget.dataset.investorBalanceCents = '';
    this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #cancel() {
    if (!this.timeout) return;

    clearTimeout(this.timeout);
    this.timeout = null;
  }

  #abort() {
    if (!this.pending) return;

    this.pending.abort();
    this.pending = null;
  }
}
